import type { FetchResult } from '@apollo/client/core'

import { isPast, subMilliseconds } from 'date-fns'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import {
  DrawChangedCause,
  type DrawChangesSubscription,
} from '#api/shared/@types/generated/subscription/graphql-schema-types'
import { LotteryCutoffTimeModal } from '#components'

import type { ScheduledDraw } from '../@types/editor'

import {
  EDITOR_CUTOFF_MODAL_SILENCE_WINDOW_MS,
  EDITOR_CUTOFF_REACHED_TOLERANCE_MS,
} from '../constants/editor'

const REFETCHING_CAUSES = [DrawChangedCause.DrawDatesChange, DrawChangedCause.JackpotsChange]

export const useEditorDraws = (lotteryId: LotteryId) => {
  const licensedTerritory = useLicensedTerritory({ raw: true })
  const { t } = useI18n()
  const { addToast } = useToaster()
  const { registerModal } = useModal()

  const { loading, refetch, result } = useScheduledDrawsQuery({
    licensedTerritory,
    limit: 2,
    lotteryId,
  })

  const { onResult } = useDrawChangesSubscription({ licensedTerritory })

  const announcedDraw = ref<Pick<ScheduledDraw, 'closingDate' | 'id'>>()
  const closedDraw = ref<ScheduledDraw>()
  const drawsResolvedAt = ref<number>()
  const upcomingDraw = ref<ScheduledDraw>()

  const scheduledDraws = computed(() => result.value?.scheduledDraws)

  const currentDraw = computed(() => scheduledDraws.value?.[0] ?? null)

  const nextDraw = computed(() => scheduledDraws.value?.[1] ?? null)

  const jackpot = computed(() => {
    if (!currentDraw.value || currentDraw.value.comingSoon) return undefined

    return currentDraw.value.jackpots.find((jackpot) => jackpot.lotteryId === lotteryId)?.value
  })

  const isCutoffReached = (closingDate: string) =>
    isPast(subMilliseconds(closingDate, EDITOR_CUTOFF_REACHED_TOLERANCE_MS))

  const isWithinSilenceWindow = () =>
    drawsResolvedAt.value !== undefined &&
    Date.now() - drawsResolvedAt.value < EDITOR_CUTOFF_MODAL_SILENCE_WINDOW_MS

  const notifyDrawDatesChanged = () =>
    addToast(
      {
        autoClose: false,
        headline: t('lottery.draw.updated.headline'),
        message: t('lottery.draw.updated.message'),
        tracking: { topicKey: 'draw_date_changed' },
      },
      'lottery-draw-updated',
    )

  const openCutoffModal = (closed: ScheduledDraw, upcoming?: ScheduledDraw) => {
    const { closingDate, id } = closed
    if (announcedDraw.value?.id === id && announcedDraw.value.closingDate === closingDate) return

    announcedDraw.value = { closingDate, id }
    closedDraw.value = closed
    upcomingDraw.value = upcoming
  }

  const clearCutoffModal = () => {
    closedDraw.value = undefined
    upcomingDraw.value = undefined
  }

  const dismissCutoffModal = () => {
    refetch()
    clearCutoffModal()
  }

  const resetCutoffAnnouncement = (draw?: ScheduledDraw) => {
    if (!draw || announcedDraw.value?.id !== draw.id || isCutoffReached(draw.closingDate)) return

    announcedDraw.value = undefined
    clearCutoffModal()
  }

  const handleDrawChanges = async ({ data }: FetchResult<DrawChangesSubscription>) => {
    if (!data?.drawChanges) return
    const { cause, closingDate, drawId, lotteryId: changedLotteryId } = data.drawChanges
    if (changedLotteryId !== lotteryId || !REFETCHING_CAUSES.includes(cause)) return

    if (cause !== DrawChangedCause.DrawDatesChange) {
      refetch()
      return
    }

    notifyDrawDatesChanged()

    const previousDraw = currentDraw.value
    const updatedDraws = (await refetch())?.data?.scheduledDraws

    if (!previousDraw || !updatedDraws) return

    const [updatedCurrentDraw] = updatedDraws
    resetCutoffAnnouncement(updatedCurrentDraw)

    if (updatedCurrentDraw?.id === previousDraw.id) return

    const closed =
      drawId === previousDraw.id && isCutoffReached(closingDate)
        ? { ...previousDraw, closingDate }
        : previousDraw

    if (!isCutoffReached(closed.closingDate)) return

    openCutoffModal(closed, updatedCurrentDraw)
  }

  const handleCutoffTimeReached = () => {
    if (!currentDraw.value) return

    if (isWithinSilenceWindow()) {
      refetch()
      return
    }

    openCutoffModal(currentDraw.value, nextDraw.value ?? undefined)
  }

  onResult(handleDrawChanges)

  watchImmediate(scheduledDraws, (draws) => {
    if (!draws || drawsResolvedAt.value !== undefined) return
    drawsResolvedAt.value = Date.now()
  })

  registerModal({
    component: LotteryCutoffTimeModal,
    onCancel: dismissCutoffModal,
    onClose: dismissCutoffModal,
    onConfirm: dismissCutoffModal,
    props: () => {
      const closed = closedDraw.value
      if (!closed) return null

      return { nextDrawDate: upcomingDraw.value?.drawDate, prevDrawDate: closed.closingDate }
    },
    stack: true,
    when: () => Boolean(closedDraw.value),
  })

  return { currentDraw, handleCutoffTimeReached, jackpot, loading, nextDraw }
}
